// ============================================================
// LateReasonModal — 遲到原因說明 Modal
// ------------------------------------------------------------
// 觸發時機：員工準備打卡，但已超過排班開始時間 + 寬限分鐘
// 用途：留下原因供主管簽核參考；存到 attendance.note
// ============================================================

import React, { useState } from 'react';

interface Props {
  scheduledStart: string; // "HH:MM"
  lateMinutes: number;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

const PRESET_REASONS = [
  '交通延誤（塞車 / 大眾運輸誤點）',
  '身體不適但仍堅持上班',
  '家庭緊急狀況',
  '天候因素（雨天 / 颱風 / 路況）',
  '個人原因',
];

export function LateReasonModal({ scheduledStart, lateMinutes, onConfirm, onCancel }: Props) {
  const [reason, setReason] = useState('');
  const [customMode, setCustomMode] = useState(false);

  const handleSubmit = () => {
    const finalReason = reason.trim();
    if (!finalReason) {
      alert('請選擇或輸入遲到原因');
      return;
    }
    onConfirm(finalReason);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-slide-up">
        {/* Header — 警示色 */}
        <div className="px-5 py-4 bg-rose-50 border-b border-rose-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-500 text-lg">
              ⏰
            </div>
            <div>
              <div className="text-base font-bold text-rose-700">您已遲到 {lateMinutes} 分鐘</div>
              <div className="text-xs text-rose-500 mt-0.5">
                排班 {scheduledStart} 開始 · 此次打卡將送主管審核
              </div>
            </div>
          </div>
        </div>

        {/* 內容 */}
        <div className="p-5 space-y-4">
          <div className="text-sm text-morandi-600">
            請說明遲到原因（必填，將記錄於出勤紀錄）：
          </div>

          {!customMode ? (
            <div className="space-y-2">
              {PRESET_REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  className={`w-full text-left px-4 py-3 rounded-btn text-sm transition-colors
                    ${reason === r
                      ? 'bg-earth-100 text-earth-700 ring-2 ring-earth-300'
                      : 'bg-morandi-50 text-morandi-600 hover:bg-morandi-100'}`}
                >
                  {r}
                </button>
              ))}
              <button
                onClick={() => { setCustomMode(true); setReason(''); }}
                className="w-full text-left px-4 py-3 rounded-btn text-sm bg-morandi-50 text-morandi-500 hover:bg-morandi-100"
              >
                ✏ 其他原因（自行輸入）
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                autoFocus
                placeholder="請描述遲到原因..."
                className="w-full px-3 py-2 border border-morandi-200 rounded-btn text-sm text-morandi-700
                           focus:outline-none focus:ring-2 focus:ring-earth-300 focus:border-transparent"
                maxLength={500}
              />
              <div className="flex justify-between text-xs">
                <button
                  onClick={() => { setCustomMode(false); setReason(''); }}
                  className="text-morandi-400 hover:text-morandi-600"
                >
                  ‹ 返回選項
                </button>
                <span className="text-morandi-300">{reason.length} / 500</span>
              </div>
            </div>
          )}

          {/* 法源提示 */}
          <div className="text-xs text-morandi-400 bg-morandi-50 rounded-lg px-3 py-2">
            💡 依勞基法 §30，雇主應如實記錄勞工出勤狀況。若有合理原因，主管簽核時可改為正常出勤。
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-3 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn text-sm font-medium"
          >
            取消打卡
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reason.trim()}
            className="flex-1 py-3 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 text-white rounded-btn text-sm font-medium"
          >
            確認打卡
          </button>
        </div>
      </div>
    </div>
  );
}
